import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ClipboardList, Calendar, Wallet, Pill, History, CalendarClock, Video, DollarSign, ShieldAlert, X, Globe, UserPlus, CalendarPlus, MessageCircle, Users } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PatientBanner } from "@/components/medico/PatientBanner";
import { AtestadoTab } from "@/components/medico/AtestadoTab";
import { AnamneseTab } from "@/components/medico/AnamneseTab";
import { AmbientScribe } from "@/components/medico/AmbientScribe";
import { GoogleAgendaCard } from "@/components/medico/GoogleAgendaCard";
import { PacientesModule, NewPatientDialog } from "@/components/pacientes/PacientesModule";
import { MemedEmbed } from "@/components/medico/MemedEmbed";
import { Telemedicina } from "@/components/medico/Telemedicina";
import { SoloCobranca } from "@/components/medico/SoloCobranca";
import { PerfilPublico } from "@/components/medico/PerfilPublico";
import { HistoricoTab } from "@/components/medico/HistoricoTab";
import { DoctorPortal } from "@/components/medicos/DoctorPortal";
import { Disponibilidade } from "@/components/Disponibilidade";
import { StatusBadge } from "@/components/StatusBadge";
import { NotificationsBell } from "@/components/NotificationsBell";
import { useAuth } from "@/store/AuthContext";
import { useClinic } from "@/store/ClinicContext";
import { INITIAL_DOCTORS } from "@/data/cfmData";
import { formatBRL, normBlocks, buildSlots, minutesOf } from "@/data/mockData";

const DAY_MAP = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];

const WA_BADGE = {
  rescheduled: ["Remarcado pelo WhatsApp", "border-violet-200 bg-violet-50 text-violet-700"],
  confirmed: ["Confirmado pelo WhatsApp", "border-emerald-200 bg-emerald-50 text-emerald-700"],
  sent: ["Lembrete enviado", "border-sky-200 bg-sky-50 text-sky-700"],
  failed: ["Lembrete falhou", "border-rose-200 bg-rose-50 text-rose-700"],
};

const WaBadge = ({ a }) => {
  const key = a.wa_reminder_status === "rescheduled" ? "rescheduled"
    : a.confirmed_by_patient && a.wa_reminder_status ? "confirmed"
    : WA_BADGE[a.wa_reminder_status] ? a.wa_reminder_status : null;
  if (!key) return null;
  const [label, cls] = WA_BADGE[key];
  const when = a.wa_reminder_at ? new Date(a.wa_reminder_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <span data-testid={`wa-badge-${a.id}`} data-status={key} title={when ? `Lembrete enviado em ${when}` : label}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      <MessageCircle className="h-3 w-3" />{label}
    </span>
  );
};

const REASON = { agendado: "já agendado", google: "ocupado no Google Agenda", passado: "horário passado" };

const SlotPicker = ({ data, date, value, onPick }) => {
  if (!data) return <p data-testid="med-slot-loading" className="text-xs text-slate-400">Carregando horários livres…</p>;
  const slots = data.slots || [];
  return (
    <div>
      <div data-testid="med-apt-availability-hint" className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-700">
        {data.has_availability
          ? `Horários livres em ${date}, iguais aos da sua página pública (já sem consultas marcadas e sem compromissos do Google Agenda).`
          : `Sua página pública não tem horários em ${date}. Configure em "Minha Página" ou digite um horário abaixo.`}
      </div>
      {slots.length > 0 && (
        <div data-testid="med-slot-grid" className="mt-2 flex flex-wrap gap-1.5">
          {slots.map((s) => (
            <button key={s.time} type="button" data-testid={`med-slot-${s.time}`} disabled={!s.free} title={s.free ? "Livre" : REASON[s.reason]}
              onClick={() => onPick(s.time)}
              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${!s.free ? "cursor-not-allowed bg-slate-100 text-slate-300 line-through" : value === s.time ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-blue-50"}`}>
              {s.time}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MinhaAgenda = ({ doctorId, doctorName }) => {
  const { appointments, patients, addAppointment, createCharge, syncCharge, healthPlans } = useClinic();
  const mine = appointments.filter((a) => !doctorId || a.doctorId === doctorId || !a.doctorId);

  const [patOpen, setPatOpen] = useState(false);
  const [aptOpen, setAptOpen] = useState(false);
  const [freeSlots, setFreeSlots] = useState(null);
  const planByName = (name) => healthPlans.find((p) => p.name === name);
  const valorFor = (conv) => Number(planByName(conv)?.valorConsulta) || 0;
  const todayISO = () => new Date(Date.now() - 3 * 3600e3).toISOString().slice(0, 10);
  const isoToBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
  const [apt, setApt] = useState({ patientId: "", patient: "", time: "", procedure: "", convenio: "Particular", value: 0, date: new Date().toISOString().slice(0, 10) });

  useEffect(() => {
    if (!aptOpen || !apt.date) { return; }
    let alive = true;
    setFreeSlots(null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clinic/free-slots?date=${apt.date}`, { headers: { Authorization: `Bearer ${localStorage.getItem("veloclin_token")}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setFreeSlots(d || { slots: [], has_availability: false }); })
      .catch(() => { if (alive) setFreeSlots({ slots: [], has_availability: false }); });
    return () => { alive = false; };
  }, [aptOpen, apt.date, appointments]);

  const cobrar = async (a) => {
    try {
      const d = await createCharge(a.id);
      if (d.email_sent) {
        toast.success("Cobrança enviada por e-mail", { description: "Link enviado ao e-mail do paciente (confira também a caixa de spam). Taxa operacional de R$10,50 descontada somente após o pagamento." });
      } else {
        try { await navigator.clipboard.writeText(d.url); } catch (err) { /* ignore */ }
        toast.warning("Link gerado — e-mail não enviado", { description: d.has_email ? "Falha no envio do e-mail. Link copiado; use o botão WhatsApp para enviar." : "Paciente sem e-mail cadastrado. Link copiado; use o botão WhatsApp para enviar." });
      }
    } catch (e) {
      toast.error("Falha ao gerar cobrança", { description: e.message });
    }
  };
  const enviarWhatsApp = async (a) => {
    try {
      let url = a.charge_url;
      let phoneRaw = a.patient_phone || "";
      if (!url) {
        const d = await createCharge(a.id);
        url = d.url;
        phoneRaw = phoneRaw || d.patient_phone || "";
      }
      const digits = phoneRaw.replace(/\D/g, "");
      const to = digits ? (digits.length <= 11 ? `55${digits}` : digits) : "";
      const msg = encodeURIComponent(`Olá ${a.patient}! Segue o link para o pagamento da sua consulta: ${url}`);
      window.open(to ? `https://wa.me/${to}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
    } catch (e) {
      toast.error("Não foi possível preparar o WhatsApp", { description: e.message });
    }
  };
  const verificarPagamento = async (a) => {
    try {
      const d = await syncCharge(a.id);
      if (d.status === "paid") toast.success("Pagamento confirmado!", { description: "Consulta liberada para telemedicina." });
      else toast("Pagamento ainda pendente", { description: "Assim que o paciente pagar, a consulta será liberada automaticamente." });
    } catch (e) {
      toast.error("Não foi possível verificar", { description: e.message });
    }
  };
  const abrirTelemed = (a) => { if (a.telemed_room) window.open(`/sala/${a.telemed_room}`, "_blank"); };

  const onPatientCreated = (created, pat) => {
    const conv = pat.convenio && planByName(pat.convenio) ? pat.convenio : "Particular";
    setApt((a) => ({ ...a, patientId: created?.id || "", patient: pat.name, convenio: conv, value: valorFor(conv), date: todayISO(), time: "" }));
    setAptOpen(true);
  };

  const saveApt = () => {
    if (!apt.patient || !apt.time) return toast.error("Selecione paciente e horário.");
    if (!apt.date) return toast.error("Selecione a data do agendamento.");
    const conflict = appointments.some((a) => a.doctorId === doctorId && a.time === apt.time && a.date === isoToBR(apt.date) && a.status !== "cancelado");
    if (conflict) {
      return toast.error("Horário já ocupado", { description: `Você já tem um paciente às ${apt.time} em ${isoToBR(apt.date)}. Escolha outro horário.` });
    }
    const patientRec = patients.find((x) => x.id === apt.patientId);
    const freeReturn = patientRec?.return_status === "active";
    addAppointment({ ...apt, date: isoToBR(apt.date), doctorId, patient_phone: patientRec?.phone || "", patient_email: patientRec?.email || "", doctor_name: doctorName || "Médico", procedure: apt.procedure || "Consulta", value: freeReturn ? 0 : (Number(apt.value) || 0), use_free_return: freeReturn });
    toast.success(freeReturn ? "Retorno gratuito agendado! (sem cobrança)" : "Agendamento criado!", { description: `${apt.patient} em ${isoToBR(apt.date)} às ${apt.time} · ${freeReturn ? "Retorno gratuito" : apt.convenio}` });
    setApt({ patientId: "", patient: "", time: "", procedure: "", convenio: "Particular", value: 0, date: todayISO() });
    setAptOpen(false);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">Minha Agenda</h3>
          <p className="text-xs text-slate-500">Seus atendimentos do dia</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="med-new-patient-btn" onClick={() => setPatOpen(true)} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"><UserPlus className="h-4 w-4 text-blue-600" /> Novo Paciente</button>
          <button data-testid="med-new-appointment-btn" onClick={() => setAptOpen(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><CalendarPlus className="h-4 w-4" /> Novo Agendamento</button>
        </div>
      </div>
      {mine.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400">Nenhum atendimento hoje.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {mine.map((a) => (
            <div
              key={a.id}
              data-testid={`minha-agenda-row-${a.id}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4 sm:flex-nowrap sm:px-6"
            >
              <span className="font-mono-num text-lg font-bold text-slate-900">{a.time}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900">{a.patient}</p>
                <p className="truncate text-sm text-slate-500">{a.procedure}{a.date ? ` · ${a.date}` : ""}</p>
              </div>
              <span className="font-mono-num hidden text-sm text-slate-400 sm:block">
                {formatBRL(a.value)}
                {a.net_value != null && <span data-testid={`med-net-${a.id}`} className="block text-[10px] text-emerald-600">líquido {formatBRL(a.net_value)}</span>}
              </span>
              <StatusBadge status={a.status} />
              <WaBadge a={a} />
              {a.charge_status === "paid" ? (
                <button data-testid={`med-telemed-${a.id}`} onClick={() => abrirTelemed(a)} className="flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100" title="Abrir teleconsulta"><Video className="h-3.5 w-3.5" /> Telemedicina</button>
              ) : (
                <div className="flex gap-1.5">
                  {a.is_return ? (
                    <span data-testid={`med-return-${a.id}`} className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-700">Retorno gratuito</span>
                  ) : (
                    <>
                      <button data-testid={`med-charge-${a.id}`} onClick={() => cobrar(a)} className="flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100" title="Gerar cobrança e enviar por e-mail"><DollarSign className="h-3.5 w-3.5" /> Cobrar</button>
                      <button data-testid={`med-wa-${a.id}`} onClick={() => enviarWhatsApp(a)} className="flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2 py-1.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-100" title="Enviar cobrança pelo meu WhatsApp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
                      {a.charge_status === "pending" && <button data-testid={`med-verify-${a.id}`} onClick={() => verificarPagamento(a)} className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100" title="Verificar pagamento">Verificar</button>}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal paciente */}
      <NewPatientDialog open={patOpen} onOpenChange={setPatOpen} onCreated={onPatientCreated} />

      {/* Modal agendamento */}
      <Dialog open={aptOpen} onOpenChange={setAptOpen}>
        <DialogContent data-testid="med-appointment-modal" className="max-w-md">
          <DialogHeader><DialogTitle>Novo Agendamento</DialogTitle><DialogDescription>Aloque um paciente na sua agenda.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold text-slate-600">Paciente</Label>
              <Select value={apt.patientId} onValueChange={(v) => {
                const p = patients.find((x) => x.id === v);
                const conv = p?.convenio && planByName(p.convenio) ? p.convenio : "Particular";
                setApt({ ...apt, patientId: v, patient: p?.name || "", convenio: conv, value: valorFor(conv) });
              }}>
                <SelectTrigger data-testid="med-apt-patient-select" className="mt-1.5"><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Data</Label><Input data-testid="med-apt-date-input" type="date" value={apt.date} min={todayISO()} onChange={(e) => setApt({ ...apt, date: e.target.value, time: "" })} className="mt-1.5" /></div>
            <SlotPicker data={freeSlots} date={isoToBR(apt.date)} value={apt.time} onPick={(s) => setApt({ ...apt, time: s })} />
            <div><Label className="text-xs font-semibold text-slate-600">Convênio</Label>
              <Select value={apt.convenio} onValueChange={(v) => setApt({ ...apt, convenio: v, value: valorFor(v) })}>
                <SelectTrigger data-testid="med-apt-convenio-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{healthPlans.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Horário</Label><Input data-testid="med-apt-time-input" type="time" value={apt.time} onChange={(e) => setApt({ ...apt, time: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Valor da consulta (R$)</Label><Input data-testid="med-apt-value-input" type="number" value={apt.value} onChange={(e) => setApt({ ...apt, value: Number(e.target.value) })} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Procedimento</Label><Input data-testid="med-apt-proc-input" value={apt.procedure} onChange={(e) => setApt({ ...apt, procedure: e.target.value })} className="mt-1.5" placeholder="Consulta" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setAptOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="med-apt-save-btn" onClick={saveApt} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">Agendar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MeuProntuario = () => {
  const { patients, grantReturn, listRecords, addRecord, downloadRecordPdf } = useClinic();
  const list = patients.map((p) => ({
    id: p.id, name: p.name,
    avatar: `https://ui-avatars.com/api/?background=2563eb&color=fff&name=${encodeURIComponent(p.name || "P")}`,
    age: p.data_nascimento || "—", plan: p.convenio || "Particular",
    lastVisit: "—", complaint: "", alerts: [], history: [],
    phone: p.phone, email: p.email, cpf: p.cpf, data_nascimento: p.data_nascimento, sexo: p.sexo,
    return_status: p.return_status, free_return_until: p.free_return_until,
  }));
  const [patientId, setPatientId] = useState("");
  const [anamnese, setAnamnese] = useState("");
  const [retDays, setRetDays] = useState(30);
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const current = list.find((p) => p.id === patientId) || list[0] || null;
  const currentId = current?.id;

  useEffect(() => {
    let active = true;
    if (!currentId) { setRecords([]); return undefined; }
    listRecords(currentId).then((recs) => { if (active) setRecords(recs); });
    return () => { active = false; };
  }, [currentId, listRecords]);

  const switchPatient = (id) => {
    setPatientId(id);
    setAnamnese("");
  };

  const saveRecord = async (withPdf) => {
    if (!anamnese.trim()) { toast.error("Escreva a evolução clínica antes de salvar."); return; }
    setSaving(true);
    try {
      const rec = await addRecord(currentId, anamnese.trim());
      setRecords((prev) => [rec, ...prev]);
      setAnamnese("");
      toast.success("Atendimento registrado no prontuário", { description: `Em ${rec.date}` });
      if (withPdf) await downloadRecordPdf(rec.id, `atendimento-${current.name}.pdf`);
    } catch (e) {
      toast.error(e.message || "Falha ao salvar atendimento");
    } finally {
      setSaving(false);
    }
  };
  const grantRet = async () => {
    try { await grantReturn(current.id, true, retDays); toast.success("Retorno gratuito concedido", { description: `Válido por ${retDays} dias.` }); }
    catch (e) { toast.error(e.message); }
  };
  const revokeRet = async () => {
    try { await grantReturn(current.id, false); toast.success("Retorno gratuito removido"); }
    catch (e) { toast.error(e.message); }
  };

  if (!current) {
    return (
      <div data-testid="prontuario-empty" className="rounded-2xl border border-slate-200/70 bg-white p-10 text-center text-sm text-slate-400">
        Nenhum paciente cadastrado ainda. Cadastre um paciente na aba “Minha Agenda”.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PatientBanner patient={current} patients={list} onSwitch={switchPatient} />
      <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="return-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-900">Retorno gratuito</p>
            {current.return_status === "active" && <p data-testid="return-status" className="text-xs font-semibold text-emerald-700">Direito ativo até {(current.free_return_until || "").split("-").reverse().join("/")}</p>}
            {current.return_status === "used" && <p data-testid="return-status" className="text-xs font-semibold text-slate-500">Retorno já utilizado</p>}
            {current.return_status === "expired" && <p data-testid="return-status" className="text-xs font-semibold text-amber-600">Validade expirada — próxima consulta é cobrada</p>}
            {(!current.return_status || current.return_status === "none") && <p data-testid="return-status" className="text-xs text-slate-400">Sem retorno ativo — próxima consulta é cobrada</p>}
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="return-days-select" value={retDays} onChange={(e) => setRetDays(Number(e.target.value))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600">
              <option value={15}>15 dias</option>
              <option value={30}>30 dias</option>
              <option value={60}>60 dias</option>
              <option value={90}>90 dias</option>
            </select>
            <button data-testid="grant-return-btn" onClick={grantRet} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">Conceder retorno</button>
            {current.return_status === "active" && <button data-testid="revoke-return-btn" onClick={revokeRet} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Remover</button>}
          </div>
        </div>
      </div>
      <AmbientScribe onResult={(t) => setAnamnese((p) => (p.trim() ? `${p.trimEnd()}\n\n${t}` : t))} />
      <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] md:p-6">
        <Tabs defaultValue="anamnese" className="w-full">
          <TabsList className="mb-5 grid w-full grid-cols-4 gap-1 rounded-xl bg-slate-100 p-1 sm:inline-grid sm:w-auto">
            <TabsTrigger value="anamnese" className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm">
              <ClipboardList className="h-4 w-4" />
              Anamnese
            </TabsTrigger>
            <TabsTrigger value="receituario" data-testid="tab-receituario" className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm">
              <Pill className="h-4 w-4" />
              <span className="hidden sm:inline">Prescrição &amp; Exames</span>
              <span className="sm:hidden">Receituário</span>
            </TabsTrigger>
            <TabsTrigger value="historico" className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
            <TabsTrigger value="atestado" data-testid="tab-atestado" className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm">
              <ClipboardList className="h-4 w-4" />
              Atestado
            </TabsTrigger>
          </TabsList>
          <TabsContent value="anamnese" className="mt-0 focus-visible:outline-none">
            <AnamneseTab value={anamnese} onChange={setAnamnese} />
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <span className="mr-auto text-xs text-slate-400">O atendimento fica registrado no prontuário com data e hora.</span>
              <button data-testid="save-record-pdf-btn" onClick={() => saveRecord(true)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60">Salvar e baixar PDF</button>
              <button data-testid="save-record-btn" onClick={() => saveRecord(false)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60">{saving ? "Salvando..." : "Salvar atendimento"}</button>
            </div>
          </TabsContent>
          <TabsContent value="receituario" className="mt-0 focus-visible:outline-none">
            <MemedEmbed patientName={current?.name} />
          </TabsContent>
          <TabsContent value="historico" className="mt-0 focus-visible:outline-none">
            <HistoricoTab
              history={records.map((r) => ({ id: r.id, title: "Atendimento clínico", doctor: `${r.author}${r.author_crm ? " · " + r.author_crm : ""}`, date: r.date, summary: r.text }))}
              onPdf={(id) => downloadRecordPdf(id, `atendimento-${current.name}.pdf`)}
            />
          </TabsContent>
          <TabsContent value="atestado" className="mt-0 focus-visible:outline-none">
            <AtestadoTab patient={current} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const MED_NAV = [
  ["prontuario", "Prontuário", ClipboardList],
  ["pacientes", "Pacientes", Users],
  ["disponibilidade", "Disponibilidade", CalendarClock],
  ["agenda", "Minha Agenda", Calendar],
  ["extrato", "Meu Extrato", Wallet],
  ["telemedicina", "Telemedicina", Video],
  ["cobranca", "Cobrança", DollarSign],
  ["perfil", "Minha Página", Globe],
];

const MedicoSidebar = ({ onLogout }) => (
  <aside data-testid="medico-sidebar" className="sticky top-0 z-40 flex h-screen w-16 shrink-0 flex-col border-r border-slate-200/70 bg-white md:w-60">
    <div className="flex h-16 items-center justify-center border-b border-slate-100 px-2 md:justify-start md:px-5">
      <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-8 w-auto object-contain md:h-10" />
    </div>
    <p className="hidden px-5 pt-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 md:block">Portal do Médico</p>
    <TabsList className="flex h-auto flex-1 flex-col items-stretch justify-start gap-1 overflow-y-auto rounded-none bg-transparent p-2 md:p-3">
      {MED_NAV.map(([v, label, Icon]) => (
        <TabsTrigger key={v} value={v} data-testid={`med-tab-${v}`} title={label} className="justify-center gap-3 rounded-xl px-0 py-3 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-50 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none md:justify-start md:px-3 md:py-2.5">
          <Icon className="h-5 w-5 shrink-0" /><span className="hidden md:inline">{label}</span>
        </TabsTrigger>
      ))}
    </TabsList>
    <button data-testid="logout-btn" onClick={onLogout} title="Sair" className="m-2 flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600 md:m-3 md:justify-start">
      <LogOut className="h-5 w-5" /><span className="hidden md:inline">Sair</span>
    </button>
  </aside>
);

export default function MedicoShell() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [show2fa, setShow2fa] = useState(true);
  const initialTab = new URLSearchParams(window.location.search).get("gcal") ? "agenda" : "prontuario";

  const myDoctorId = currentUser?.doctorId ?? currentUser?.doctor_id;
  const doctorsList = useMemo(() => {
    if (myDoctorId && !INITIAL_DOCTORS.find((d) => d.id === myDoctorId)) {
      return [
        { id: myDoctorId, name: currentUser?.name || "Meu perfil", specialty: currentUser?.specialty || "Clínico", crm: currentUser?.crm || "—", uf: currentUser?.uf || "", repasse: 100, avatar: currentUser?.avatar },
        ...INITIAL_DOCTORS,
      ];
    }
    return INITIAL_DOCTORS;
  }, [myDoctorId, currentUser]);

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <Tabs defaultValue={initialTab} orientation="vertical" className="flex min-h-screen bg-[#F8FAFC]">
      <MedicoSidebar onLogout={doLogout} />
      <div className="min-w-0 flex-1">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-end gap-3 px-4 py-3 md:px-8">

          <div className="flex items-center gap-3">
            <div
              data-testid="medico-badge"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5"
            >
              {currentUser?.avatar && (
                <img src={currentUser.avatar} alt={currentUser.name} className="h-7 w-7 rounded-full object-cover" />
              )}
              <div className="leading-tight">
                <p className="text-xs font-bold text-slate-800">Logado como: {currentUser?.name}</p>
                <p className="text-[11px] text-slate-400">
                  {currentUser?.specialty || "Médico"}{currentUser?.repasse ? ` · Repasse ${currentUser.repasse}%` : " · VeloClin Solo"}
                </p>
              </div>
            </div>
            <NotificationsBell />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-3 py-6 sm:px-5 md:px-8 md:py-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Meu Espaço Clínico
          </h1>
          <p className="text-sm text-slate-500">
            Prontuário, agenda e seus repasses — acesso exclusivo e isolado.
          </p>
        </div>

        {show2fa && (
          <div data-testid="twofa-banner" className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-nowrap">
            <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
            <p className="min-w-0 flex-1 basis-40 text-sm text-amber-800">
              Recomendamos ativar a Autenticação em Duas Etapas (2FA) para maior segurança dos prontuários.
            </p>
            <button
              data-testid="twofa-config-btn"
              onClick={() => toast("Configurações de Perfil em breve", { description: "A ativação do 2FA estará disponível nas Configurações de Perfil." })}
              className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-600"
            >
              Configurar Agora
            </button>
            <button data-testid="twofa-dismiss-btn" onClick={() => setShow2fa(false)} className="shrink-0 text-amber-400 transition-colors hover:text-amber-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

          <TabsContent value="prontuario" className="mt-0 focus-visible:outline-none">
            <MeuProntuario />
          </TabsContent>
          <TabsContent value="disponibilidade" className="mt-0 focus-visible:outline-none">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <h3 className="font-heading text-base font-bold text-slate-900">Minha Disponibilidade</h3>
              <p className="mb-5 text-xs text-slate-500">Defina os turnos em que sua agenda fica aberta. A recepção vê isto em tempo real.</p>
              <div className="max-w-xl">
                <Disponibilidade doctorId={myDoctorId} />
              </div>
            </div>
          </TabsContent>
          <TabsContent value="pacientes" className="mt-0 focus-visible:outline-none">
            <PacientesModule />
          </TabsContent>
          <TabsContent value="agenda" className="mt-0 focus-visible:outline-none">
            <GoogleAgendaCard />
            <MinhaAgenda doctorId={myDoctorId} doctorName={currentUser?.name} />
          </TabsContent>
          <TabsContent value="extrato" className="mt-0 focus-visible:outline-none">
            <DoctorPortal doctors={doctorsList} lockedDoctorId={myDoctorId} />
          </TabsContent>
          <TabsContent value="telemedicina" className="mt-0 focus-visible:outline-none">
            <Telemedicina />
          </TabsContent>
          <TabsContent value="cobranca" className="mt-0 focus-visible:outline-none">
            <SoloCobranca />
          </TabsContent>
          <TabsContent value="perfil" className="mt-0 focus-visible:outline-none">
            <PerfilPublico />
          </TabsContent>
      </main>
      </div>
    </Tabs>
  );
}
